// Providers admin route — /admin/providers
//
// CRUD + connectivity ping for the three provider kinds we support
// (claude_cli, codex_cli, openai_http). The form is kind-conditional: CLI
// kinds expose binary_path/home_dir/base_args/env_overrides, while
// openai_http exposes base_url/api_key/extra_headers. config_json is a free
// JSON blob — we render the per-kind fields by parsing/patching that JSON
// in place so the wire format stays exactly what the server expects.

import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Badge,
  Button,
  ConfirmDialog,
  FormRow,
  Input,
  Select,
  Table,
  Textarea,
  Toggle,
} from "@/admin-design/_common"
import { llmApi, type Provider, type ProviderInput, type ProviderKind } from "@/api/llm"

type FormState = {
  name: string
  kind: ProviderKind
  enabled: boolean
  concurrency: string
  // claude_cli / codex_cli
  binary_path: string
  home_dir: string
  base_args: string // JSON
  env_overrides: string // JSON
  // openai_http
  base_url: string
  api_key: string
  extra_headers: string // JSON
}

// Per-kind defaults applied when adding a new provider or switching kind in
// the Add form. Edit mode does not re-apply these (existing config wins).
const KIND_DEFAULTS: Record<ProviderKind, Partial<FormState>> = {
  claude_cli: { binary_path: "/usr/local/bin/claude", home_dir: "/home/llmuser" },
  codex_cli: { binary_path: "/usr/local/bin/codex", home_dir: "/home/llmuser" },
  opencode_cli: { binary_path: "/usr/local/bin/opencode", home_dir: "/home/llmuser" },
  openai_http: {},
}

const EMPTY_FORM: FormState = {
  name: "",
  kind: "claude_cli",
  enabled: true,
  concurrency: "1",
  binary_path: KIND_DEFAULTS.claude_cli.binary_path ?? "",
  home_dir: KIND_DEFAULTS.claude_cli.home_dir ?? "",
  base_args: "[]",
  env_overrides: "{}",
  base_url: "",
  api_key: "",
  extra_headers: "{}",
}

const KIND_OPTIONS = [
  { label: "Claude CLI", value: "claude_cli" },
  { label: "Codex CLI", value: "codex_cli" },
  { label: "OpenCode CLI", value: "opencode_cli" },
  { label: "OpenAI HTTP", value: "openai_http" },
]

function safeParse<T>(s: string, fallback: T): T {
  try {
    const parsed = JSON.parse(s)
    return parsed as T
  } catch {
    return fallback
  }
}

function fromProvider(p: Provider): FormState {
  const cfg = safeParse<Record<string, unknown>>(p.config_json || "{}", {})
  const f: FormState = { ...EMPTY_FORM, name: p.name, kind: p.kind, enabled: p.enabled, concurrency: String(p.concurrency || 1) }
  if (p.kind === "openai_http") {
    f.base_url = String(cfg.base_url ?? "")
    f.extra_headers = JSON.stringify(cfg.extra_headers ?? {}, null, 2)
  } else {
    f.binary_path = String(cfg.binary_path ?? "")
    f.home_dir = String(cfg.home_dir ?? "")
    f.base_args = JSON.stringify(cfg.base_args ?? [], null, 2)
    f.env_overrides = JSON.stringify(cfg.env_overrides ?? {}, null, 2)
  }
  return f
}

/** Build the wire payload from form state. Returns either `{ ok, payload }` or `{ error }`. */
function toPayload(f: FormState, isCreate: boolean): { ok: true; payload: ProviderInput } | { ok: false; error: string } {
  if (!f.name.trim()) return { ok: false, error: "name is required" }
  let cfg: Record<string, unknown> = {}
  if (f.kind === "openai_http") {
    if (!f.base_url.trim()) return { ok: false, error: "base_url is required for openai_http" }
    cfg.base_url = f.base_url.trim()
    if (f.extra_headers.trim()) {
      try {
        cfg.extra_headers = JSON.parse(f.extra_headers)
      } catch {
        return { ok: false, error: "extra_headers must be valid JSON" }
      }
    }
  } else {
    if (!f.binary_path.trim()) return { ok: false, error: "binary_path is required" }
    cfg.binary_path = f.binary_path.trim()
    if (f.home_dir.trim()) cfg.home_dir = f.home_dir.trim()
    if (f.base_args.trim()) {
      try {
        cfg.base_args = JSON.parse(f.base_args)
      } catch {
        return { ok: false, error: "base_args must be valid JSON (array)" }
      }
    }
    if (f.env_overrides.trim()) {
      try {
        cfg.env_overrides = JSON.parse(f.env_overrides)
      } catch {
        return { ok: false, error: "env_overrides must be valid JSON (object)" }
      }
    }
  }
  const concurrency = Math.max(1, Number.parseInt(f.concurrency, 10) || 1)
  const payload: ProviderInput = {
    name: f.name.trim(),
    kind: f.kind,
    config_json: JSON.stringify(cfg),
    enabled: f.enabled,
    concurrency,
  }
  // api_key only sent if user typed one; on edit, blank means "leave existing".
  if (f.kind === "openai_http" && (f.api_key || isCreate)) {
    if (f.api_key) payload.api_key = f.api_key
  }
  return { ok: true, payload }
}

function PingBadge({ status }: { status?: string }) {
  if (!status) return <span className="text-muted-foreground">—</span>
  if (status === "ok") return <Badge variant="success">ok</Badge>
  return (
    <Badge variant="destructive" title={status}>
      error
    </Badge>
  )
}

export default function Providers() {
  const qc = useQueryClient()
  const providers = useQuery({ queryKey: ["admin", "providers"], queryFn: llmApi.listProviders })

  const [editing, setEditing] = useState<Provider | null>(null)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [formError, setFormError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Provider | null>(null)
  const [pingStatus, setPingStatus] = useState<{ id: number; status: string } | null>(null)

  const openCreate = () => {
    setForm(EMPTY_FORM)
    setFormError(null)
    setCreating(true)
  }
  const openEdit = (p: Provider) => {
    setForm(fromProvider(p))
    setFormError(null)
    setEditing(p)
  }
  const closeForm = () => {
    setCreating(false)
    setEditing(null)
    setFormError(null)
  }

  const createMut = useMutation({
    mutationFn: (body: ProviderInput) => llmApi.createProvider(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "providers"] })
      closeForm()
    },
    onError: (e: Error) => setFormError(e.message),
  })
  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<ProviderInput> }) => llmApi.updateProvider(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "providers"] })
      closeForm()
    },
    onError: (e: Error) => setFormError(e.message),
  })
  const deleteMut = useMutation({
    mutationFn: (id: number) => llmApi.deleteProvider(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "providers"] })
      setConfirmDelete(null)
    },
  })
  const pingMut = useMutation({
    mutationFn: (id: number) => llmApi.pingProvider(id),
    onSuccess: (res, id) => {
      setPingStatus({ id, status: res.status })
      qc.invalidateQueries({ queryKey: ["admin", "providers"] })
    },
    onError: (e: Error, id) => setPingStatus({ id, status: "error: " + e.message }),
  })

  const onSubmit = () => {
    const built = toPayload(form, !editing)
    if (!built.ok) {
      setFormError(built.error)
      return
    }
    if (editing) {
      updateMut.mutate({ id: editing.id, body: built.payload })
    } else {
      createMut.mutate(built.payload)
    }
  }

  const isModalOpen = creating || editing !== null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-foreground">Providers</h1>
        <Button size="sm" onClick={openCreate}>
          + Add provider
        </Button>
      </div>

      {pingStatus && (
        <div
          className={`rounded-md border px-3 py-2 text-sm ${
            pingStatus.status === "ok"
              ? "border-success/30 bg-success-soft text-success-soft-foreground"
              : "border-destructive/30 bg-destructive-soft text-destructive-soft-foreground"
          }`}
        >
          <span className="font-medium">Ping #{pingStatus.id}:</span>{" "}
          <span className="font-mono text-xs">{pingStatus.status}</span>
          <button
            type="button"
            onClick={() => setPingStatus(null)}
            className="ml-3 text-xs underline-offset-2 hover:underline"
          >
            dismiss
          </button>
        </div>
      )}

      <div className="rounded-lg border border-border bg-card">
        <Table>
          <Table.Head>
            <Table.Row interactive={false}>
              <Table.Header>Name</Table.Header>
              <Table.Header>Kind</Table.Header>
              <Table.Header>Concurrency</Table.Header>
              <Table.Header>Enabled</Table.Header>
              <Table.Header>Last ping</Table.Header>
              <Table.Header className="text-right">Actions</Table.Header>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {providers.isLoading && (
              <Table.Row interactive={false}>
                <Table.Cell colSpan={6} className="text-center text-muted-foreground">
                  Loading…
                </Table.Cell>
              </Table.Row>
            )}
            {providers.data && providers.data.length === 0 && (
              <Table.Empty
                colSpan={6}
                title="No providers yet"
                description="Add a Claude CLI, Codex CLI, or OpenAI-compatible HTTP provider to start serving traffic."
                action={
                  <Button size="sm" onClick={openCreate}>
                    + Add provider
                  </Button>
                }
              />
            )}
            {providers.data?.map((p) => (
              <Table.Row key={p.id}>
                <Table.Cell className="font-medium">{p.name}</Table.Cell>
                <Table.Cell>
                  <code className="text-xs text-muted-foreground">{p.kind}</code>
                </Table.Cell>
                <Table.Cell>{p.concurrency}</Table.Cell>
                <Table.Cell>
                  {p.enabled ? <Badge variant="success">on</Badge> : <Badge>off</Badge>}
                </Table.Cell>
                <Table.Cell>
                  <PingBadge status={p.last_ping_status} />
                </Table.Cell>
                <Table.Cell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button
                      variant="secondary"
                      size="xs"
                      loading={pingMut.isPending && pingMut.variables === p.id}
                      onClick={() => pingMut.mutate(p.id)}
                    >
                      Test
                    </Button>
                    <Button variant="secondary" size="xs" onClick={() => openEdit(p)}>
                      Edit
                    </Button>
                    <Button variant="destructive" size="xs" onClick={() => setConfirmDelete(p)}>
                      Delete
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>

      {isModalOpen && (
        <ProviderFormModal
          form={form}
          setForm={setForm}
          formError={formError}
          isEdit={editing !== null}
          isPending={createMut.isPending || updateMut.isPending}
          onClose={closeForm}
          onSubmit={onSubmit}
          existingHasSecret={editing?.has_secret ?? false}
        />
      )}

      <ConfirmDialog
        open={confirmDelete !== null}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={`Delete provider “${confirmDelete?.name ?? ""}”?`}
        description="This removes the provider permanently and unregisters it from the dispatcher. Models pointing at it will fail with 404 until re-pointed."
        confirmLabel="Delete"
        confirmVariant="destructive"
        loading={deleteMut.isPending}
        onConfirm={() => {
          if (confirmDelete) deleteMut.mutate(confirmDelete.id)
        }}
      />
    </div>
  )
}

interface ProviderFormModalProps {
  form: FormState
  setForm: (next: FormState) => void
  formError: string | null
  isEdit: boolean
  isPending: boolean
  onClose: () => void
  onSubmit: () => void
  existingHasSecret: boolean
}

function ProviderFormModal({
  form,
  setForm,
  formError,
  isEdit,
  isPending,
  onClose,
  onSubmit,
  existingHasSecret,
}: ProviderFormModalProps) {
  const isHttp = form.kind === "openai_http"
  const title = isEdit ? "Edit provider" : "Add provider"

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm({ ...form, [key]: value })

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          className="w-full max-w-xl rounded-lg border border-border bg-card shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-base font-semibold text-card-foreground">{title}</h2>
          </div>
          <div className="max-h-[70vh] overflow-y-auto px-6 py-4 space-y-4">
            <FormRow label="Name" required stacked>
              <Input
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="claude-prod"
              />
            </FormRow>
            <FormRow label="Kind" required stacked>
              <Select
                value={form.kind}
                onValueChange={(v) => {
                  const next = v as ProviderKind
                  // On Add, apply per-kind defaults (binary_path / home_dir)
                  // when switching kinds so operators don't have to remember
                  // /usr/local/bin/claude etc. Edit mode disables this select.
                  const defaults = KIND_DEFAULTS[next]
                  setForm({
                    ...form,
                    kind: next,
                    binary_path: defaults.binary_path ?? "",
                    home_dir: defaults.home_dir ?? "",
                  })
                }}
                options={KIND_OPTIONS}
                disabled={isEdit}
              />
            </FormRow>
            <FormRow label="Concurrency" stacked help="Maximum parallel requests routed to this provider.">
              <Input
                type="number"
                min={1}
                value={form.concurrency}
                onChange={(e) => update("concurrency", e.target.value)}
              />
            </FormRow>
            <FormRow label="Enabled" stacked>
              <Toggle checked={form.enabled} onChange={(v) => update("enabled", v)} />
            </FormRow>

            {isHttp ? (
              <>
                <FormRow label="Base URL" required stacked>
                  <Input
                    value={form.base_url}
                    onChange={(e) => update("base_url", e.target.value)}
                    placeholder="https://api.openai.com/v1"
                  />
                </FormRow>
                <FormRow
                  label="API key"
                  stacked
                  help={
                    isEdit && existingHasSecret
                      ? "Leave blank to keep the existing key."
                      : "Stored encrypted at rest."
                  }
                >
                  <Input
                    type="password"
                    revealable
                    value={form.api_key}
                    onChange={(e) => update("api_key", e.target.value)}
                    placeholder={isEdit && existingHasSecret ? "•••••••• (unchanged)" : "sk-…"}
                  />
                </FormRow>
                <FormRow label="Extra headers (JSON)" stacked help='e.g. {"X-Custom": "value"}'>
                  <Textarea
                    rows={4}
                    value={form.extra_headers}
                    onChange={(e) => update("extra_headers", e.target.value)}
                    className="font-mono text-xs"
                  />
                </FormRow>
              </>
            ) : (
              <>
                <FormRow label="Binary path" required stacked>
                  <Input
                    value={form.binary_path}
                    onChange={(e) => update("binary_path", e.target.value)}
                    placeholder="/usr/local/bin/claude"
                  />
                </FormRow>
                <FormRow label="Home dir" stacked help="Override $HOME passed to the spawned CLI.">
                  <Input
                    value={form.home_dir}
                    onChange={(e) => update("home_dir", e.target.value)}
                    placeholder="/var/lib/llm-saas/claude-home"
                  />
                </FormRow>
                <FormRow label="Base args (JSON array)" stacked>
                  <Textarea
                    rows={3}
                    value={form.base_args}
                    onChange={(e) => update("base_args", e.target.value)}
                    className="font-mono text-xs"
                  />
                </FormRow>
                <FormRow label="Env overrides (JSON object)" stacked>
                  <Textarea
                    rows={3}
                    value={form.env_overrides}
                    onChange={(e) => update("env_overrides", e.target.value)}
                    className="font-mono text-xs"
                  />
                </FormRow>
              </>
            )}

            {formError && (
              <div className="rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2 text-sm text-destructive-soft-foreground">
                {formError}
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/50 px-6 py-3">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button size="sm" loading={isPending} onClick={onSubmit}>
              {isEdit ? "Save" : "Create"}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
